import { useEffect, useMemo, useState } from 'react';
import {
  calculerTotalCommande,
  COMMANDE_STATUTS,
  COMMANDE_STATUT_LABELS,
  totalDiverge,
  type Client,
  type Commande,
  type CommandeStatut,
  type MatiereMonture,
} from '@sirajoptic/shared';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { InputField, SelectField } from '@/components/ui/field';
import { Encart } from '@/components/ui/feedback';
import { useToast } from '@/components/ui/toast';
import { ClientAutocomplete } from '@/features/clients/components/ClientAutocomplete';
import { ApiError } from '@/lib/api-client';
import { aujourdhui, dh } from '@/lib/format';
import { useCreerCommande, useModifierCommande, useSocietes } from '../api/commandes.api';
import { MontureInput } from './MontureInput';
import { MesuresBloc, OrdonnanceBloc } from './OrdonnanceInput';

type Formulaire = {
  numero_bon: string;
  date_commande: string;
  client_id: string | null;
  client_nom: string;
  client_tel: string;
  od_raw: string;
  og_raw: string;
  addition: string;
  prescripteur: string;
  date_ordonnance: string;
  ecart_od: string;
  ecart_og: string;
  hauteur_od: string;
  hauteur_og: string;
  progression_od: string;
  progression_og: string;
  largeur_od: string;
  largeur_og: string;
  diagonale_od: string;
  diagonale_og: string;
  matiere_monture: MatiereMonture | null;
  type_verre: string;
  prix_verre: string;
  monture: string;
  prix_monture: string;
  lentilles: string;
  prix_lentilles: string;
  /** Rattachement au stock, nul tant que le poste est en saisie libre. */
  verre_product_id: string | null;
  monture_product_id: string | null;
  lentilles_product_id: string | null;
  societe: string;
  total: string;
  statut: CommandeStatut;
};

function vide(): Formulaire {
  return {
    numero_bon: '',
    date_commande: aujourdhui(),
    client_id: null,
    client_nom: '',
    client_tel: '',
    od_raw: '',
    og_raw: '',
    addition: '',
    prescripteur: '',
    date_ordonnance: '',
    ecart_od: '',
    ecart_og: '',
    hauteur_od: '',
    hauteur_og: '',
    progression_od: '',
    progression_og: '',
    largeur_od: '',
    largeur_og: '',
    diagonale_od: '',
    diagonale_og: '',
    matiere_monture: null,
    type_verre: '',
    prix_verre: '',
    monture: '',
    prix_monture: '',
    lentilles: '',
    prix_lentilles: '',
    verre_product_id: null,
    monture_product_id: null,
    lentilles_product_id: null,
    societe: '',
    total: '',
    statut: 'EN_COURS',
  };
}

function depuisCommande(c: Commande): Formulaire {
  return {
    numero_bon: c.numero_bon ?? '',
    date_commande: c.date_commande,
    client_id: c.client_id,
    client_nom: c.client_nom,
    client_tel: c.client_tel ?? '',
    od_raw: c.od_raw ?? '',
    og_raw: c.og_raw ?? '',
    addition: c.addition ?? '',
    prescripteur: c.prescripteur ?? '',
    date_ordonnance: c.date_ordonnance ?? '',
    ecart_od: c.ecart_od ?? '',
    ecart_og: c.ecart_og ?? '',
    hauteur_od: c.hauteur_od ?? '',
    hauteur_og: c.hauteur_og ?? '',
    progression_od: c.progression_od ?? '',
    progression_og: c.progression_og ?? '',
    largeur_od: c.largeur_od ?? '',
    largeur_og: c.largeur_og ?? '',
    diagonale_od: c.diagonale_od ?? '',
    diagonale_og: c.diagonale_og ?? '',
    matiere_monture: c.matiere_monture,
    type_verre: c.type_verre ?? '',
    prix_verre: c.prix_verre === null ? '' : String(c.prix_verre),
    monture: c.monture ?? '',
    prix_monture: c.prix_monture === null ? '' : String(c.prix_monture),
    lentilles: c.lentilles ?? '',
    prix_lentilles: c.prix_lentilles === null ? '' : String(c.prix_lentilles),
    verre_product_id: c.verre_product_id,
    monture_product_id: c.monture_product_id,
    lentilles_product_id: c.lentilles_product_id,
    societe: c.societe ?? '',
    total: String(c.total),
    statut: c.statut,
  };
}

const nombreOuNull = (v: string) => (v.trim() === '' ? null : Number(v.replace(',', '.')));
const texteOuNull = (v: string) => (v.trim() === '' ? null : v.trim());

export function CommandeForm({
  ouvert,
  commande,
  clientInitial,
  onFermer,
  onEnregistre,
}: {
  ouvert: boolean;
  /** null => création. */
  commande: Commande | null;
  /** Pré-remplissage depuis la fiche d'un client. */
  clientInitial?: {
    client: Client;
    derniereOrdonnance: Commande | null;
    /** Séparée : les mesures ne viennent pas forcément de la même commande. */
    dernieresMesures?: Commande | null;
  } | null;
  onFermer: () => void;
  onEnregistre?: (commande: Commande) => void;
}) {
  const [form, setForm] = useState<Formulaire>(vide);
  /* L'état d'ouverture, conservé pour savoir si quelque chose a bougé depuis.
   * Sans lui, impossible de distinguer une fermeture anodine d'une saisie
   * perdue — et le formulaire compte quinze champs. */
  const [initial, setInitial] = useState<Formulaire>(vide);
  const [totalTouche, setTotalTouche] = useState(false);
  const [erreurs, setErreurs] = useState<Record<string, string>>({});

  const creer = useCreerCommande();
  const modifier = useModifierCommande();
  const { data: societes } = useSocietes();
  const toast = useToast();

  useEffect(() => {
    if (!ouvert) return;
    setErreurs({});

    if (commande) {
      const depart = depuisCommande(commande);
      setForm(depart);
      setInitial(depart);
      setTotalTouche(true);
      return;
    }

    const base = vide();
    if (clientInitial) {
      const { client, derniereOrdonnance, dernieresMesures } = clientInitial;
      base.client_id = client.id;
      base.client_nom = client.nom;
      base.client_tel = client.tel ?? '';

      // La dernière ordonnance connue sert de point de départ : l'opticien
      // n'a plus qu'à corriger l'écart depuis la visite précédente.
      if (derniereOrdonnance) {
        base.od_raw = derniereOrdonnance.od_raw ?? '';
        base.og_raw = derniereOrdonnance.og_raw ?? '';
        base.addition = derniereOrdonnance.addition ?? '';
        /* Ni le prescripteur ni la date, en revanche : ce sont les
         * caractéristiques d'une prescription précise. Les recopier ferait
         * reposer la nouvelle commande sur une ordonnance périmée, et fausserait
         * le délai de renouvellement AMO sans que rien ne le signale. */
      }

      /* Seul l'écart pupillaire est repris.
       *
       * C'est la seule cote qui appartient au visage : elle ne bouge plus chez
       * l'adulte, et la reprendre évite une remesure à chaque paire — y compris
       * quand la dernière commande n'était qu'un changement de verres.
       *
       * Les quatre autres appartiennent à la monture ou au verre de cette
       * commande-ci. Une hauteur, une largeur, une diagonale se mesurent sur le
       * calibre choisi aujourd'hui ; les recopier de la paire précédente ferait
       * hériter les cotes de l'ancienne monture, et le laboratoire taillerait
       * juste — pour la mauvaise forme. Un champ vide se remarque ; une valeur
       * plausible et fausse, non. */
      if (dernieresMesures) {
        base.ecart_od = dernieresMesures.ecart_od ?? '';
        base.ecart_og = dernieresMesures.ecart_og ?? '';
      }
    }
    setForm(base);
    setInitial(base);
    setTotalTouche(false);
  }, [ouvert, commande, clientInitial]);

  /* Comparaison de l'état complet : plus fiable qu'un drapeau posé à la main
   * dans chacun des `set()`, qu'un champ ajouté plus tard oublierait. */
  const modifie = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(initial),
    [form, initial],
  );

  const prix = useMemo(
    () => ({
      prix_verre: nombreOuNull(form.prix_verre),
      prix_monture: nombreOuNull(form.prix_monture),
      prix_lentilles: nombreOuNull(form.prix_lentilles),
    }),
    [form.prix_verre, form.prix_monture, form.prix_lentilles],
  );

  const somme = calculerTotalCommande(prix);

  // Règle 8 : le total est préremplli par la somme tant qu'il n'a pas été
  // saisi à la main, mais il reste modifiable et c'est la saisie qui gagne.
  const totalEffectif = totalTouche && form.total.trim() !== '' ? Number(form.total) : somme;
  const ecart = totalTouche && form.total.trim() !== '' && totalDiverge(Number(form.total), prix);

  function set<K extends keyof Formulaire>(cle: K, valeur: Formulaire[K]) {
    setForm((f) => ({ ...f, [cle]: valeur }));
  }


  function valider(): boolean {
    const e: Record<string, string> = {};
    if (!form.client_nom.trim()) e.client_nom = 'Le nom du client est obligatoire.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.date_commande)) e.date_commande = 'Date invalide.';
    setErreurs(e);
    return Object.keys(e).length === 0;
  }

  async function soumettre() {
    if (!valider()) return;

    const charge = {
      numero_bon: texteOuNull(form.numero_bon),
      date_commande: form.date_commande,
      client_id: form.client_id,
      client_nom: form.client_nom.trim(),
      client_tel: texteOuNull(form.client_tel),
      od_raw: texteOuNull(form.od_raw),
      og_raw: texteOuNull(form.og_raw),
      addition: texteOuNull(form.addition),
      prescripteur: texteOuNull(form.prescripteur),
      date_ordonnance: texteOuNull(form.date_ordonnance),
      ecart_od: texteOuNull(form.ecart_od),
      ecart_og: texteOuNull(form.ecart_og),
      hauteur_od: texteOuNull(form.hauteur_od),
      hauteur_og: texteOuNull(form.hauteur_og),
      progression_od: texteOuNull(form.progression_od),
      progression_og: texteOuNull(form.progression_og),
      largeur_od: texteOuNull(form.largeur_od),
      largeur_og: texteOuNull(form.largeur_og),
      diagonale_od: texteOuNull(form.diagonale_od),
      diagonale_og: texteOuNull(form.diagonale_og),
      matiere_monture: form.matiere_monture,
      type_verre: texteOuNull(form.type_verre),
      prix_verre: prix.prix_verre,
      monture: texteOuNull(form.monture),
      prix_monture: prix.prix_monture,
      lentilles: texteOuNull(form.lentilles),
      prix_lentilles: prix.prix_lentilles,
      verre_product_id: form.verre_product_id,
      monture_product_id: form.monture_product_id,
      lentilles_product_id: form.lentilles_product_id,
      societe: texteOuNull(form.societe),
      total: totalEffectif,
      statut: form.statut,
    };

    try {
      const enregistree = commande
        ? await modifier.mutateAsync({ id: commande.id, patch: charge })
        : await creer.mutateAsync(charge);

      toast.succes(commande ? 'Commande mise à jour.' : 'Commande enregistrée.');
      onEnregistre?.(enregistree);
      onFermer();
    } catch (e) {
      if (e instanceof ApiError) {
        setErreurs(e.details ?? {});
        toast.erreur(e.message);
      } else {
        toast.erreur("La commande n'a pas pu être enregistrée.");
      }
    }
  }

  const enCours = creer.isPending || modifier.isPending;

  return (
    <Dialog
      ouvert={ouvert}
      onFermer={onFermer}
      titre={commande ? 'Modifier la commande' : 'Nouvelle commande'}
      // Pendant l'enregistrement, il n'y a plus rien à perdre.
      modifie={modifie && !enCours}
      /* En création, pas de sous-titre : « reprend les colonnes du cahier »
       * ne servait qu'une fois, et coûtait une ligne à chaque commande. */
      description={
        commande
          ? `${commande.client_nom} — toute commande reste modifiable, quel que soit son statut.`
          : undefined
      }
      taille="plein"
      pied={
        <>
          {/* Le rappel vit à côté des boutons plutôt que dans le formulaire :
              il concerne ce qui se passe *après* la création, c'est donc au
              moment de cliquer qu'il sert. */}
          {!commande ? (
            <p className="mr-auto self-center text-xs text-ink-muted">
              Les paiements s'ajoutent depuis le détail de la commande, une fois celle-ci créée.
            </p>
          ) : null}
          <Button variant="outline" onClick={onFermer} disabled={enCours}>
            Annuler
          </Button>
          <Button variant="primary" onClick={() => void soumettre()} chargement={enCours}>
            {commande ? 'Enregistrer' : 'Créer la commande'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {commande?.a_verifier ? (
          <Encart ton="moutarde" titre="Ligne importée à vérifier">
            Corrigez les champs signalés puis enregistrez : le drapeau disparaîtra.
            {commande.import_raw ? (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs font-medium underline">
                  Donnée d'origine
                </summary>
                <pre className="mt-1 max-h-40 overflow-auto rounded bg-white p-2 text-[11px] text-ink-soft">
                  {JSON.stringify(commande.import_raw, null, 2)}
                </pre>
              </details>
            ) : null}
          </Encart>
        ) : null}

        {/* Identité — l'ordre des champs suit celui de sa feuille. Sur grand
            écran, les six tiennent sur une ligne : c'est ce qui libère la
            hauteur pour que le formulaire entier se lise sans défiler. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          <InputField
            label="Date"
            type="date"
            value={form.date_commande}
            onChange={(e) => set('date_commande', e.target.value)}
            erreur={erreurs.date_commande}
            requis
          />
          <ClientAutocomplete
            nom={form.client_nom}
            // Le numéro saisi juste à côté part dans la fiche créée : le
            // ressaisir ensuite depuis l'écran Clients serait absurde.
            tel={form.client_tel}
            onChangeNom={(nom) => set('client_nom', nom)}
            onSelectionner={(client) => {
              set('client_id', client?.id ?? null);
              if (client?.tel) set('client_tel', client.tel);
            }}
            erreur={erreurs.client_nom}
            requis
            className="lg:col-span-2"
          />
          <InputField
            label="N° Tel"
            value={form.client_tel}
            onChange={(e) => set('client_tel', e.target.value)}
          />
          <InputField
            label="N° de Bon"
            value={form.numero_bon}
            onChange={(e) => set('numero_bon', e.target.value)}
            className="font-mono"
          />
          {/* Les sociétés déjà connues restent proposées par la liste, dès
              qu'on entre dans le champ : c'était l'utilité réelle de l'exemple
              grisé, sans son défaut — se lire comme une saisie. */}
          <InputField
            label="Société"
            list="societes"
            value={form.societe}
            onChange={(e) => set('societe', e.target.value)}
          />
          <datalist id="societes">
            {(societes ?? []).map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
          <SelectField
            label="Statut"
            value={form.statut}
            onChange={(e) => set('statut', e.target.value as CommandeStatut)}
          >
            {COMMANDE_STATUTS.map((s) => (
              <option key={s} value={s}>
                {COMMANDE_STATUT_LABELS[s]}
              </option>
            ))}
          </SelectField>
        </div>

        {/* Deux colonnes sur grand écran. À gauche ce qui se relève sur le
            client — correction et cotes ; à droite ce qui se chiffre. Empilé,
            le formulaire faisait deux écrans de haut et « Créer la commande »
            n'apparaissait qu'après défilement. En dessous de 1 280 px la pile
            revient : des colonnes étroites couperaient les champs. */}
        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] xl:items-start">
          <div className="space-y-3">
            <OrdonnanceBloc
              od={form.od_raw}
              og={form.og_raw}
              addition={form.addition}
              prescripteur={form.prescripteur}
              dateOrdonnance={form.date_ordonnance}
              onChange={(champ, valeur) => set(champ, valeur)}
            />

            <MesuresBloc
              valeurs={{
                ecart_od: form.ecart_od,
                ecart_og: form.ecart_og,
                hauteur_od: form.hauteur_od,
                hauteur_og: form.hauteur_og,
                progression_od: form.progression_od,
                progression_og: form.progression_og,
                largeur_od: form.largeur_od,
                largeur_og: form.largeur_og,
                diagonale_od: form.diagonale_od,
                diagonale_og: form.diagonale_og,
              }}
              matiere={form.matiere_monture}
              onChangeMatiere={(m) => set('matiere_monture', m)}
              onChange={(champ, valeur) => set(champ, valeur)}
            />
          </div>

          <div className="space-y-3">
            {/* Postes chiffrés : verre, monture, lentilles. */}
            <div className="space-y-3 rounded-md border border-line p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">Postes</p>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr]">
                <InputField
                  label="Type de verre"
                  value={form.type_verre}
                  onChange={(e) => set('type_verre', e.target.value)}
                />
                <InputField
                  label="Prix verre (DH)"
                  type="number"
                  step="0.01"
                  value={form.prix_verre}
                  onChange={(e) => set('prix_verre', e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr]">
                <MontureInput
                  valeur={form.monture}
                  productId={form.monture_product_id}
                  /* Le texte change : la fiche ne décrit plus ce qui est écrit sur
                     le bon, donc le lien tombe. Sans quoi le laboratoire monterait
                     un modèle et le stock en décompterait un autre. */
                  onChangeTexte={(texte) =>
                    setForm((f) => ({ ...f, monture: texte, monture_product_id: null }))
                  }
                  /* Un choix au stock renseigne les trois d'un coup : le libellé
                     imprimé sur le bon, le prix de vente de la fiche, et la
                     référence qui fera sortir l'exemplaire à la livraison. */
                  onChoisirProduit={(produit) =>
                    setForm((f) => ({
                      ...f,
                      monture: produit.nom,
                      prix_monture: String(produit.prix_vente),
                      monture_product_id: produit.id,
                    }))
                  }
                />
                <InputField
                  label="Prix monture (DH)"
                  type="number"
                  step="0.01"
                  value={form.prix_monture}
                  onChange={(e) => set('prix_monture', e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr]">
                <InputField
                  label="Lentilles / produits"
                  value={form.lentilles}
                  onChange={(e) => set('lentilles', e.target.value)}
                />
                <InputField
                  label="Prix lentilles (DH)"
                  type="number"
                  step="0.01"
                  value={form.prix_lentilles}
                  onChange={(e) => set('prix_lentilles', e.target.value)}
                />
              </div>
            </div>

            {/* Total : prérempli, mais toujours modifiable. */}
            <div className="flex flex-wrap items-end justify-between gap-4 rounded-md border border-line bg-surface-sunken p-3">
              <InputField
                label="Total (DH)"
                type="number"
                step="0.01"
                className="w-40"
                value={totalTouche ? form.total : String(somme)}
                onChange={(e) => {
                  setTotalTouche(true);
                  set('total', e.target.value);
                }}
                aide={`Somme des postes : ${dh(somme)}`}
              />
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide text-ink-muted">Total retenu</p>
                <p className="text-2xl font-semibold tabular text-mustard-600">{dh(totalEffectif)}</p>
              </div>
            </div>

            {ecart ? (
              <Encart ton="moutarde" titre="Le total diffère de la somme des postes">
                C'est autorisé — la valeur saisie est conservée telle quelle, comme sur la feuille.
              </Encart>
            ) : null}

          </div>
        </div>
      </div>
    </Dialog>
  );
}
